import DonutChart from "Components/DonutChart";
import { formatWon, formatKoreanUnit } from "Components/Sales/salesFormat";

export const UNMATCHED_COLORS = {
  "설문 미응답": "#cbd5e1",
  "수동 결제": "#94a3b8",
};
const MATCHED_COLOR = "#2563eb";

export const toRevenueChartData = (items = [], metric) => {
  let dividerPlaced = false;
  return items
    .filter((item) => item[metric] > 0)
    .map((item) => {
      const row = {
        label: item.label,
        count: item[metric],
        sub: `결제 ${item.payment_count.toLocaleString()}건`,
      };
      if (!item.unmatched) return row;
      const separated = !dividerPlaced;
      dividerPlaced = true;
      return {
        ...row,
        color: UNMATCHED_COLORS[item.label] || UNMATCHED_COLORS["설문 미응답"],
        separated,
      };
    });
};

export const buildMatchShares = (items = [], metric, total) => {
  const amountOf = (label) =>
    items.find((item) => item.unmatched && item.label === label)?.[metric] || 0;
  const unanswered = amountOf("설문 미응답");
  const manual = amountOf("수동 결제");
  const pct = (value) => (total ? (value / total) * 100 : 0);
  return {
    matched: pct(total - unanswered - manual),
    unanswered: pct(unanswered),
    manual: pct(manual),
  };
};

const styles = {
  resultArea: { display: "flex", flexDirection: "column", gap: "24px" },
  summaryCard: {
    display: "flex",
    alignItems: "flex-end",
    gap: "40px",
    flexWrap: "wrap",
    padding: "28px",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    backgroundColor: "#ffffff",
  },
  summaryLabel: { fontSize: "15px", color: "#6b7280", marginBottom: "8px" },
  summaryValue: { fontSize: "40px", fontWeight: "700", color: "#1f2937", lineHeight: 1.1 },
  summarySub: { display: "flex", gap: "22px", flexWrap: "wrap", fontSize: "14px", color: "#4b5563", paddingBottom: "6px" },
  matchBar: { width: "240px", maxWidth: "100%", paddingBottom: "6px" },
  track: { display: "flex", height: "8px", borderRadius: "4px", overflow: "hidden", backgroundColor: "#e5e7eb" },
  fill: { height: "100%" },
  matchCaption: { display: "flex", gap: "12px", flexWrap: "wrap", fontSize: "12px", color: "#6b7280", marginTop: "6px" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(460px, 1fr))", gap: "24px" },
  card: { border: "1px solid #e5e7eb", borderRadius: "12px", padding: "28px", backgroundColor: "#ffffff" },
  cardTitle: { fontSize: "16px", fontWeight: "600", color: "#1f2937", marginBottom: "20px" },
  note: {
    marginTop: "16px",
    fontSize: "12.5px",
    lineHeight: 1.55,
    color: "#92400e",
    backgroundColor: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: "8px",
    padding: "8px 12px",
  },
};

const SurveyRevenueView = ({ data, metric, periodLabel, sections }) => {
  const metricLabel = metric === "net" ? "순매출" : "매출";
  const total = (metric === "net" ? data?.net_total : data?.gross_total) || 0;
  const shares = buildMatchShares(data?.inflow?.items, metric, total);

  return (
    <div style={styles.resultArea}>
      <div style={styles.summaryCard}>
        <div>
          <div style={styles.summaryLabel}>{periodLabel} {metricLabel}</div>
          <div style={styles.summaryValue} data-testid="revenue-total">{formatWon(total)}</div>
        </div>
        <div style={styles.summarySub}>
          <span>결제 <strong>{(data?.payment_count || 0).toLocaleString()}건</strong></span>
          <span>결제자 <strong>{(data?.payer_count || 0).toLocaleString()}명</strong></span>
        </div>
        {total > 0 && (
          <div style={styles.matchBar}>
            <div style={styles.track}>
              <div style={{ ...styles.fill, width: `${shares.matched}%`, backgroundColor: MATCHED_COLOR }} />
              <div style={{ ...styles.fill, width: `${shares.unanswered}%`, backgroundColor: UNMATCHED_COLORS["설문 미응답"] }} />
              <div style={{ ...styles.fill, width: `${shares.manual}%`, backgroundColor: UNMATCHED_COLORS["수동 결제"] }} />
            </div>
            <div style={styles.matchCaption}>
              <span>설문 연결 {shares.matched.toFixed(1)}%</span>
              <span>미응답 {shares.unanswered.toFixed(1)}%</span>
              <span>수동 {shares.manual.toFixed(1)}%</span>
            </div>
          </div>
        )}
      </div>

      <div style={styles.grid}>
        {sections.map((section) => (
          <div key={section.key} style={styles.card} data-testid={`revenue-${section.key}`}>
            <div style={styles.cardTitle}>{section.label}</div>
            <DonutChart
              unit="원"
              data={toRevenueChartData(data?.[section.key]?.items, metric)}
              percentBase={total}
              centerValue={formatKoreanUnit(total)}
              centerLabel={`총 ${metricLabel}(원)`}
            />
            {section.key === "interest" && (
              <div style={styles.note}>
                관심 서비스는 여러 개를 고를 수 있어 항목마다 결제 금액 전체를 더합니다. 그래서 항목 합계가
                총 {metricLabel}보다 클 수 있고, 비율은 총액 대비 해당 항목 선택자의 {metricLabel}입니다.
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SurveyRevenueView;
